import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Slot, Slottable } from "@radix-ui/react-slot";
import { cva } from "class-variance-authority";
import * as React from "react";
import { wrapperTypographyClassNames } from "../../typography";
import { cn } from "../../lib/utils";
export const buttonVariants = cva(`inline-flex shrink-0 items-center justify-center gap-1 rounded-md whitespace-nowrap ${wrapperTypographyClassNames.button} transition-[background-color,border-color,color,opacity] outline-none focus-visible:ring-2 focus-visible:ring-offset-0 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4`, {
    variants: {
        variant: {
            default: "bg-primary text-primary-foreground hover:bg-primary/80 focus-visible:ring-primary/40",
            destructive: "bg-destructive text-white hover:bg-destructive/80 focus-visible:ring-destructive/40",
            outline: "border border-border bg-transparent text-foreground hover:bg-accent/40 focus-visible:ring-border/60",
            secondary: "bg-secondary text-secondary-foreground hover:bg-secondary/80 focus-visible:ring-border/60",
            ghost: "text-foreground hover:bg-accent/40 focus-visible:ring-border/60",
            link: "text-primary underline-offset-4 hover:underline focus-visible:ring-primary/30",
        },
        size: {
            default: "h-10 min-h-10 min-w-10 px-4 py-2.5 has-[>svg]:px-3",
            xs: "h-8 min-h-8 min-w-8 px-2 py-1.5 has-[>svg]:px-2",
            sm: "h-9 min-h-9 min-w-9 px-3 py-2 has-[>svg]:px-3",
            icon: "size-10 min-h-10 min-w-10 p-0",
        },
        iconPosition: {
            none: null,
            left: null,
            right: null,
            both: null,
        },
    },
    compoundVariants: [
        {
            size: ["default", "sm"],
            iconPosition: "left",
            class: "px-3",
        },
        {
            size: ["default", "sm"],
            iconPosition: "right",
            class: "px-3",
        },
        {
            size: ["default", "sm"],
            iconPosition: "both",
            class: "px-3",
        },
        {
            size: "xs",
            iconPosition: "left",
            class: "px-2",
        },
        {
            size: "xs",
            iconPosition: "right",
            class: "px-3",
        },
        {
            size: "xs",
            iconPosition: "both",
            class: "pr-3 pl-2",
        },
        {
            size: "icon",
            variant: "link",
            class: "px-0",
        },
    ],
    defaultVariants: {
        variant: "default",
        size: "default",
        iconPosition: "none",
    },
});
const warnedTextOnlyButtons = new Set();
function getIconPosition({ leftIcon, rightIcon, }) {
    if (leftIcon && rightIcon) {
        return "both";
    }
    if (leftIcon) {
        return "left";
    }
    if (rightIcon) {
        return "right";
    }
    return "none";
}
function isTextOnlyNode(node) {
    if (node === null ||
        node === undefined ||
        typeof node === "boolean" ||
        typeof node === "string" ||
        typeof node === "number") {
        return true;
    }
    if (Array.isArray(node)) {
        return node.every(isTextOnlyNode);
    }
    if (!React.isValidElement(node)) {
        return false;
    }
    if (node.type === React.Fragment || typeof node.type === "string") {
        return isTextOnlyNode(node.props
            .children);
    }
    return false;
}
function getTextOnlyLabel(node) {
    if (node === null || node === undefined || typeof node === "boolean") {
        return "";
    }
    if (typeof node === "string" || typeof node === "number") {
        return String(node);
    }
    if (Array.isArray(node)) {
        return node.map(getTextOnlyLabel).join(" ").trim();
    }
    if (!React.isValidElement(node)) {
        return "";
    }
    if (node.type === React.Fragment || typeof node.type === "string") {
        return getTextOnlyLabel(node.props
            .children);
    }
    return "";
}
function warnOnImplicitTextOnlyButton({ allowTextOnly, children, leftIcon, rightIcon, size, }) {
    if (process.env.NODE_ENV !== "development") {
        return;
    }
    if (allowTextOnly || leftIcon || rightIcon || size === "icon") {
        return;
    }
    if (!isTextOnlyNode(children)) {
        return;
    }
    const normalizedLabel = getTextOnlyLabel(children).replace(/\s+/g, " ").trim();
    const warningKey = normalizedLabel || "<empty>";
    if (warnedTextOnlyButtons.has(warningKey)) {
        return;
    }
    warnedTextOnlyButtons.add(warningKey);
    console.warn(`[Design System Button] Text-only buttons should be explicit exceptions. Add a meaningful left or right icon when it improves recognition, or pass allowTextOnly for intentional text-only usage. Do not add generic arrows or decorative icons just to satisfy the rule. Label: "${warningKey}"`);
}
function Button({ allowTextOnly, asChild = false, children, className, leftIcon, rightIcon, size, variant, ...props }) {
    const Comp = asChild ? Slot : "button";
    const iconPosition = getIconPosition({ leftIcon, rightIcon });
    warnOnImplicitTextOnlyButton({
        allowTextOnly,
        children,
        leftIcon,
        rightIcon,
        size,
    });
    return (_jsxs(Comp, { "data-slot": "button", className: cn(buttonVariants({ variant, size, iconPosition, className })), ...props, children: [leftIcon ? (_jsx("span", { className: "shrink-0 [&>svg]:size-4", children: leftIcon })) : null, _jsx(Slottable, { children: children }), rightIcon ? (_jsx("span", { className: "shrink-0 [&>svg]:size-4", children: rightIcon })) : null] }));
}
export { Button };
